import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import juniorBianchim from "@/assets/junior-bianchim.jpg";
import mentoria2 from "@/assets/mentoria-2.jpg";
import mentoria3 from "@/assets/mentoria-3.jpg";
import mentoria6 from "@/assets/mentoria-6.jpg";
import coverCopa from "@/assets/cover-licoes-copa-2026.jpg";
import {
  ArrowRight,
  Clock,
  User,
  Target,
  Lightbulb,
  Trophy,
  Zap,
  Shield,
  AlertTriangle,
  CheckCircle,
  BookOpen,
  Users,
  Repeat,
  TrendingUp,
  Activity,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const LicoesCopa2026Vendas = () => {
  const navigate = useNavigate();

  const licoes = [
    {
      number: "01",
      title: "Ninguém Vence Copa Sem Treinar Todo Dia",
      subtitle: "Talento sem repetição é aposta, não estratégia",
      description:
        "Modric aos 40 anos ainda faz aquecimento de base. Mbappé treina finalização mesmo depois de marcar 3 gols no jogo anterior. Vinícius Jr passa 90 minutos treinando dribles que o torcedor vê em 4 segundos. Em vendas é igual: o vendedor que só 'entra em campo' na reunião com o cliente joga no escuro. Time comercial campeão treina abordagem, contorno de objeção e fechamento toda semana — não quando 'sobrar tempo'.",
      example:
        '"Meu time bate meta há 3 trimestres, pra que treinar mais?" — Foi exatamente o que a Alemanha pensou em 2018 antes de cair na fase de grupos.',
      tip: "Treino não é o que você faz quando está mal. É o que impede você de ficar mal.",
      icon: Repeat,
    },
    {
      number: "02",
      title: "O Jogo é Decidido no Minuto 90 (ou no Fim do Mês)",
      subtitle: "Fechamento é resistência, não sorte",
      description:
        "A Copa 2026 já mostrou 4 jogos decididos nos acréscimos e 2 nos pênaltis até as semis. Times que não treinam a pressão final perdem. Em vendas, é o mesmo: 68% dos vendedores desistem antes do 5º follow-up, mas 80% das vendas B2B fecham entre o 5º e o 12º contato (Sales Insights Lab, 2026). Fechar é jogar até o apito final, com o pulso firme quando o cliente diz 'me liga semana que vem'.",
      example:
        '"Não vou insistir pra não parecer chato." — É o mesmo pensamento do goleiro que fecha o olho antes do pênalti.',
      tip: "Quem treina cobrança de pênalti não sente medo. Quem treina fechamento não sente vergonha.",
      icon: Target,
    },
    {
      number: "03",
      title: "Sem Coach no Banco, o Time Improvisa",
      subtitle: "Gestor comercial é técnico, não juiz",
      description:
        "Nenhuma seleção da Copa 2026 chegou às quartas sem uma comissão técnica que analisa vídeo, corrige posicionamento e ajusta a estratégia entre jogos. E o seu gestor comercial? Ele assiste reunião do time, ouve ligação gravada, faz role-play semanal? Ou só cobra número em reunião de segunda? Um estudo do Harvard Business Review (2026) mostra que times com 1h de coaching semanal por vendedor batem meta 27% mais que times sem coaching.",
      example:
        '"Aqui a gente não faz reunião de coaching, o pessoal já sabe vender." — Tradução: o time está sozinho no gramado.',
      tip: "Coaching semanal não é micro-gestão. É o que separa o Real Madrid do time de várzea.",
      icon: Users,
    },
    {
      number: "04",
      title: "Análise de Vídeo: O Segredo Que a IA Levou Para Vendas",
      subtitle: "Você revê suas ligações como Ancelotti revê os jogos?",
      description:
        "Toda seleção na Copa 2026 tem departamento de análise que decompõe cada jogada. Em vendas, a IA generativa está fazendo o mesmo: transcrever, analisar e pontuar cada reunião. Ferramentas de conversation intelligence cresceram 340% no B2B em 2026 (Gartner). O vendedor que revê a própria ligação com IA identifica em 15 minutos padrões que levaria meses para perceber sozinho — objeções mais frequentes, momento em que perdeu o cliente, tempo falando vs. escutando.",
      example:
        '"Fiz 40 ligações essa semana." — Mas quantas você reviu? Ronaldo Fenômeno revia partida por partida em 1998. Neymar revê VAR de cada jogo em 2026.',
      tip: "Volume sem análise é ansiedade disfarçada de produtividade.",
      icon: Activity,
    },
    {
      number: "05",
      title: "Time Que Não Treina Junto, Perde Junto",
      subtitle: "Cultura comercial se constrói em coletivo, não em planilha",
      description:
        "A Argentina de 2022 (e a de 2026, favorita nas quartas) tem um mantra: 'somos família'. Em vendas B2B, 61% das empresas que crescem 2 dígitos ao ano têm rituais semanais de time comercial — não reuniões de status, mas sessões de troca de casos, role-play e celebração de deals fechados (McKinsey Sales Growth, 2026). Vendedor isolado no home office fechando sozinho não faz cultura. Faz turnover.",
      example:
        "Quando o Palmeiras venceu a Libertadores 2021, o zagueiro pediu pra treinar cobrança de falta que o atacante fazia. Isso é time.",
      tip: "Se seu vendedor não sabe como o outro do lado dele vende, você tem uma soma de pessoas — não um time.",
      icon: Users,
    },
    {
      number: "06",
      title: "Prepare-se Para o Adversário, Não Para o Roteiro",
      subtitle: "Cada cliente joga um esquema tático diferente",
      description:
        "Nenhuma seleção da Copa 2026 entra em campo sem estudar o adversário: quem chuta com esquerda, quem sofre pela direita, qual jogador tem cartão amarelo. Em vendas, o vendedor médio abre o LinkedIn do cliente 30 segundos antes da call. O vendedor que fecha grande contrato estuda: notícia recente da empresa, fundraising, cargo do decisor, últimas contratações e concorrentes ganhando terreno. Cada cliente exige um plano diferente — script único é auto-sabotagem.",
      example:
        '"Meu pitch funciona pra qualquer cliente." — Foi o que a Espanha pensou em 2014 antes de tomar 5x1 da Holanda com esquema novo.',
      tip: "Personalização não é escrever o nome do cliente no e-mail. É saber por que ele vai comprar antes dele saber.",
      icon: Shield,
    },
    {
      number: "07",
      title: "Reserva Preparada é o Que Ganha Copa",
      subtitle: "Sua bancada de vendedores sabe entrar em campo?",
      description:
        "Em nenhuma final de Copa desde 2010 o time campeão terminou o jogo com os mesmos 11 do início. Bancada preparada decide. Em times comerciais, o que garante escala é ter novos vendedores prontos em 30-45 dias — não em 6 meses. Onboarding com IA, simulações realistas e biblioteca de casos gravados reduzem o tempo de rampa em até 60% (LinkedIn Sales State of Sales, 2026). Se você depende só dos seus 2 top performers, um pé torcido derruba sua meta anual.",
      example:
        '"Meu vendedor novo aprende no campo, com o tempo." — Tempo é o que você não tem no fim de trimestre.',
      tip: "Time forte é aquele em que o 12º homem faz a diferença. Não aquele em que só o camisa 10 aparece.",
      icon: TrendingUp,
    },
  ];

  const dadosCopa = [
    { valor: "5 dias", label: "para a final da Copa 2026 (19/07)" },
    { valor: "80%", label: "das vendas B2B fecham após o 5º follow-up" },
    { valor: "27%", label: "mais meta batida com coaching semanal" },
    { valor: "60%", label: "redução no tempo de rampa com IA no treino" },
  ];

  const errosComuns = [
    "Achar que reunião de segunda é 'treino comercial'",
    "Só revisar ligação quando o vendedor perdeu a venda",
    "Não ter um plano de treino contínuo por vendedor",
    "Confundir volume de atividade com performance real",
    "Deixar o vendedor novo aprender sozinho 'no gramado'",
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline:
      "Lições da Copa do Mundo 2026 Para Times de Vendas: Não Perca o Gol na Hora do Pênalti",
    description:
      "A 5 dias da final da Copa 2026, veja 7 lições dos gramados que separam times comerciais campeões dos que desperdiçam a meta no minuto 90.",
    author: { "@type": "Person", name: "Junior Bianchim" },
    publisher: { "@type": "Organization", name: "Vendas Trainer" },
    datePublished: "2026-07-14",
    dateModified: "2026-07-14",
  };

  return (
    <>
      <SEOHead
        title="Lições da Copa 2026 Para Vendas: Não Perca o Gol na Hora do Pênalti"
        description="A 5 dias da final da Copa 2026, veja 7 lições dos gramados que separam times comerciais campeões dos que perdem a meta no minuto 90. Treino contínuo com IA."
        keywords="copa 2026 vendas, treinamento comercial, treinador de vendas com IA, alta performance em vendas, coaching comercial, fechamento de vendas, treino contínuo, gestão comercial"
        canonicalUrl="https://bianchimbusiness.com.br/posts/licoes-copa-2026-vendas"
        ogType="article"
        articlePublishedTime="2026-07-14T00:00:00Z"
        articleAuthor="Junior Bianchim"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10">
        <Header />

        <main id="main-content">
          <article>
            {/* Hero */}
            <section className="relative">
              <div className="w-full h-[50vh] sm:h-[60vh] relative overflow-hidden">
                <img
                  src={coverCopa}
                  alt="Estádio da Copa 2026 com equipe comercial celebrando gol"
                  className="w-full h-full object-cover"
                  width={1600}
                  height={912}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              </div>
              <div className="container mx-auto px-4 relative -mt-32 sm:-mt-40 z-10">
                <div className="max-w-4xl mx-auto">
                  <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4">
                    <Trophy className="w-4 h-4" aria-hidden="true" />
                    Alta Performance · Copa 2026
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-foreground">
                    Lições da Copa 2026 Para Vendas:{" "}
                    <span className="text-gradient-brand">não perca o gol na hora do pênalti</span>
                  </h1>
                  <p className="text-lg text-muted-foreground max-w-3xl">
                    A 5 dias da final da Copa do Mundo 2026, os times comerciais campeões
                    estão fazendo o mesmo que Mbappé, Modric e Vinícius Jr:
                    <strong className="text-foreground"> treinam todo dia — mesmo quando estão vencendo</strong>.
                  </p>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" aria-hidden="true" />
                      <span>Junior Bianchim</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" aria-hidden="true" />
                      <span>10 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" aria-hidden="true" />
                      <time dateTime="2026-07-14">14 Jul 2026</time>
                    </div>
                  </div>
                </div>
              </div>
            </section>


            {/* Intro + números */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-foreground">
                    A Copa Acaba Dia 19. Sua Meta Acaba Dia 31.
                  </h2>
                  <div className="space-y-4 text-muted-foreground text-lg leading-relaxed">
                    <p>
                      Hoje é <strong className="text-foreground">14 de julho de 2026</strong>.
                      Faltam 5 dias para a final da Copa do Mundo — a maior competição
                      esportiva do planeta, com 48 seleções, US$ 11 bilhões movimentados
                      e uma média de 3,7 bilhões de pessoas assistindo cada jogo da fase
                      final (FIFA, 2026).
                    </p>
                    <p>
                      Enquanto o mundo comemora gols, times comerciais brasileiros
                      travam sua própria copa: <strong className="text-foreground">o fechamento do 1º semestre</strong>.
                      E a verdade dura é que a maioria vai chegar ao dia 31 de julho
                      com a bola nos pés e sem coragem de chutar. Vai perder venda no
                      pênalti porque não treinou pênalti.
                    </p>
                    <p>
                      Nas últimas semanas, analisamos o que a Copa 2026 já ensinou —
                      quartas de final, viradas nos acréscimos, seleções eliminadas por
                      excesso de confiança — e traduzimos em <strong className="text-foreground">7 lições diretas para o seu time de vendas</strong>.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
                    {dadosCopa.map((d, i) => (
                      <Card
                        key={i}
                        className="p-5 text-center border-primary/20 bg-card"
                      >
                        <div className="text-2xl sm:text-3xl font-extrabold text-primary mb-1">
                          {d.valor}
                        </div>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          {d.label}
                        </p>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* 7 Lições */}
            <section className="py-12 sm:py-20">
              <div className="container mx-auto px-4">
                <div className="text-center mb-10 sm:mb-16">
                  <h2 className="text-2xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      As 7 Lições dos Gramados Para o Seu Time
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Cada lição é um paralelo direto entre o que a Copa 2026 ensinou e
                    o que separa times comerciais campeões de times comerciais
                    exaustos.
                  </p>
                </div>

                <div className="space-y-12 sm:space-y-20 max-w-5xl mx-auto">
                  {licoes.map((licao, index) => {
                    const Icon = licao.icon;
                    const isEven = index % 2 === 0;
                    const imgs = [
                      mentoria3,
                      mentoria6,
                      mentoria2,
                      mentoria3,
                      mentoria6,
                      mentoria2,
                      juniorBianchim,
                    ];

                    return (
                      <div
                        key={licao.number}
                        className={`flex flex-col ${
                          isEven ? "md:flex-row" : "md:flex-row-reverse"
                        } gap-6 sm:gap-12 items-center`}
                      >
                        <div className="flex-1 w-full">
                          <div className="flex items-center gap-3 sm:gap-4 mb-4">
                            <span className="text-4xl sm:text-6xl font-bold text-primary/20">
                              {licao.number}
                            </span>
                            <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0">
                              <Icon
                                className="w-5 h-5 sm:w-7 sm:h-7 text-white"
                                aria-hidden="true"
                              />
                            </div>
                          </div>
                          <h3 className="text-xl sm:text-2xl font-bold mb-2 text-foreground">
                            {licao.title}
                          </h3>
                          <p className="text-base sm:text-lg text-primary mb-3 sm:mb-4">
                            {licao.subtitle}
                          </p>
                          <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
                            {licao.description}
                          </p>

                          <blockquote className="border-l-4 border-accent pl-3 sm:pl-4 py-2 mb-4 sm:mb-6 bg-accent/5 rounded-r-lg">
                            <p className="italic text-sm sm:text-base text-foreground">
                              {licao.example}
                            </p>
                          </blockquote>

                          <div className="flex items-start gap-3 p-4 rounded-lg bg-card border border-primary/20">
                            <Lightbulb
                              className="w-5 h-5 text-primary mt-0.5 flex-shrink-0"
                              aria-hidden="true"
                            />
                            <p className="text-sm text-muted-foreground">
                              <strong className="text-foreground">
                                Dica do treinador:
                              </strong>{" "}
                              {licao.tip}
                            </p>
                          </div>
                        </div>

                        <div className="flex-1 w-full">
                          <img
                            src={imgs[index]}
                            alt={`${licao.title} — treinamento comercial Junior Bianchim`}
                            className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
                            loading="lazy"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* CTA principal — Treinador de vendas com IA */}
            <section className="py-12 sm:py-20">
              <div className="container mx-auto px-4">
                <Card className="p-6 sm:p-12 bg-gradient-to-br from-primary via-accent to-secondary border-0 text-center relative overflow-hidden max-w-5xl mx-auto">
                  <div
                    className="absolute inset-0 bg-cover bg-center opacity-10"
                    style={{ backgroundImage: `url(${coverCopa})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-accent/90 to-secondary/90" />

                  <div className="relative z-10">
                    <Trophy
                      className="w-12 h-12 sm:w-16 sm:h-16 text-white mx-auto mb-4 sm:mb-6"
                      aria-hidden="true"
                    />
                    <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 text-white">
                      Seu Time Está Treinando Mês a Mês
                      <br />
                      Para Não Errar o Pênalti?
                    </h2>
                    <p className="text-base sm:text-xl text-white/90 max-w-2xl mx-auto mb-6 sm:mb-8">
                      No <strong>Vendas Trainer</strong>, sua equipe treina abordagem,
                      contorno de objeção e fechamento com <strong>IA que simula
                      clientes reais</strong> — todo mês, com feedback instantâneo e
                      ranking. Como Copa: quem treina mais, decide o jogo.
                    </p>
                    <Button
                      size="lg"
                      onClick={() => navigate("/auth")}
                      className="bg-white text-primary hover:bg-white/90 text-lg px-10 py-6 shadow-2xl"
                    >
                      Testar o Treinador de Vendas com IA Grátis
                      <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                    </Button>
                    <p className="text-sm text-white/80 mt-4">
                      ✓ Sem cartão &nbsp; ✓ Acesso imediato &nbsp; ✓ 10 simulações grátis
                    </p>
                  </div>
                </Card>
              </div>
            </section>

            {/* Erros comuns */}
            <section className="py-12 sm:py-20 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="text-center mb-10">
                  <h2 className="text-2xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-destructive to-destructive/70 bg-clip-text text-transparent">
                      5 Erros Que Fazem Times Comerciais Perderem no Minuto 90
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Se você reconhecer 3 ou mais desses erros no seu time hoje, o
                    problema não é meta — é preparo.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
                  {errosComuns.map((erro, i) => (
                    <Card
                      key={i}
                      className="p-5 border-destructive/30 bg-destructive/5 flex items-start gap-3"
                    >
                      <AlertTriangle
                        className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                      <p className="text-sm text-foreground">{erro}</p>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* Fechamento */}
            <section className="py-12 sm:py-20">
              <div className="container mx-auto px-4">
                <div className="max-w-3xl mx-auto text-center">
                  <BookOpen
                    className="w-12 h-12 text-primary mx-auto mb-6"
                    aria-hidden="true"
                  />
                  <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-foreground">
                    A Final da Copa é Dia 19. A Sua é Todo Dia.
                  </h2>
                  <p className="text-muted-foreground text-lg mb-4 leading-relaxed">
                    Ganhar Copa não é acaso — é a soma silenciosa de anos de treino
                    invisível para o torcedor. Bater meta comercial não é sorte — é a
                    soma silenciosa de simulações, coaching semanal e revisão de cada
                    reunião.
                  </p>
                  <p className="text-foreground font-semibold text-lg mb-8">
                    Enquanto seus concorrentes esperam a próxima Copa, seu time pode
                    começar a treinar hoje.
                  </p>
                  <Button
                    size="lg"
                    onClick={() => navigate("/auth")}
                    className="bg-gradient-to-r from-primary to-accent hover:opacity-90 text-lg px-8"
                  >
                    Começar a Treinar Agora
                    <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </section>

            <PageNavigation />
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default LicoesCopa2026Vendas;
